<template>
  <div class="relative">
    <div v-if="!showControl.hideRemove" class="remove-setting-block-btn" @click="$emit('remove')">
      <SVGIcon icon="common-trash" fill="var(--gray-60)" />
      <p>{{ displayText.delete }}</p>
    </div>
    <EnabledNotificationBlock :value.sync="syncForm.enabled" />
    <SendChannelOptionsBlock :value.sync="syncForm.channels" :notifyIndex="notifyIndex" />
    <NotificationAttachConditionBlock :value.sync="syncForm.targetCondition" />
    <SendTimeBlock :notifyIndex="notifyIndex" :value.sync="syncForm.sendTimeSettings" />
    <BaseElFormItem required class="block-form-item">
      <template #label>
        <div class="flex items-center justify-between" style="width: calc(50% - 10px)">
          <p>{{ displayText.notificationContent }}</p>
          <BaseElButton type="text" class="underline text-primary-100" @click="hidePreview = !hidePreview">{{ hidePreview ? displayText.showPreview : displayText.hidePreview }}</BaseElButton>
        </div>
      </template>
      <div class="grid grid-cols-2 gap-[20px]">
        <div v-if="!showControl.isDefault" class="flex flex-col gap-[20px]">
          <!-- 圖文資訊 -->
          <div>
            <p class="hint-text">{{ displayText.imageText.hint }}</p>
            <ToggleNotificationContentBlock
              v-if="showControl.imageText"
              :title="contentBlockTypeConfigs.imageText.label"
              :notifyIndex="notifyIndex"
              :contentIndex="contentIndexOf('imageText')"
              :inactiveText="`${displayText.activeText} ${contentBlockTypeConfigs.imageText.label}`"
              :enabled.sync="imageTextBlockEnabled"
              :contentTemplate.sync="imageTextBlockTemplateId"
              :contentTemplateOptions="imageTextContentTemplateOptions"
            >
              <template #tooltip>{{ displayText.imageText.tooltip }}</template>
            </ToggleNotificationContentBlock>
          </div>
        </div>
        <div v-if="showControl.isDefault" class="flex flex-col gap-[20px]">
          <!-- 預約資訊 -->
          <ReservationInfoBlock
            v-if="showControl.appointmentInfo"
            :notifyIndex="notifyIndex"
            :contentIndex="contentIndexOf('appointmentInfo')"
            :title="contentBlockTypeConfigs.appointmentInfo.label"
            :inactiveText="`${displayText.activeText} ${contentBlockTypeConfigs.appointmentInfo.label}`"
            :contentData.sync="appointmentInfoBlock"
            :contentTemplateOptions="appointmentInfoContentTemplateOptions"
            :enableReply="reservationConfig.enableReply"
          />
          <!-- 店家資訊 -->
          <ToggleNotificationContentBlock
            v-if="showControl.shopInfo"
            :title="contentBlockTypeConfigs.shopInfo.label"
            :notifyIndex="notifyIndex"
            :contentIndex="contentIndexOf('shopInfo')"
            :inactiveText="`${displayText.activeText} ${contentBlockTypeConfigs.shopInfo.label}`"
            :enabled.sync="shopInfoEnabled"
            :contentTemplate.sync="shopInfoTemplateId"
            :contentTemplateOptions="shopInfoContentTemplateOptions"
          >
            <template #tooltip>{{ displayText.shopInfo.tooltip }}</template>
          </ToggleNotificationContentBlock>
          <!-- 帳號資訊 -->
          <ToggleNotificationContentBlock
            v-if="showControl.memberInfo"
            :title="contentBlockTypeConfigs.memberInfo.label"
            :notifyIndex="notifyIndex"
            :contentIndex="contentIndexOf('memberInfo')"
            :inactiveText="`${displayText.activeText} ${contentBlockTypeConfigs.memberInfo.label}`"
            :enabled.sync="memberInfoEnabled"
            :contentTemplate.sync="memberInfoTemplateId"
            :titleTooltip="displayText.memberInfo.tooltip"
            hideForm
          />
          <!-- 推薦加購商品/服務 -->
          <UpsellRecommendationBlock
            v-if="showControl.upsellRecommendation"
            :title="contentBlockTypeConfigs.upsellRecommendation.label"
            :inactiveText="`${displayText.activeText} ${contentBlockTypeConfigs.upsellRecommendation.label}`"
            :enabled.sync="upsellRecommendationEnabled"
          />
          <!-- 圖文資訊 -->
          <ToggleNotificationContentBlock
            v-if="showControl.imageText"
            :title="contentBlockTypeConfigs.imageText.label"
            :notifyIndex="notifyIndex"
            :contentIndex="contentIndexOf('imageText')"
            :inactiveText="`${displayText.activeText} ${contentBlockTypeConfigs.imageText.label}`"
            :enabled.sync="imageTextBlockEnabled"
            :contentTemplate.sync="imageTextBlockTemplateId"
            :contentTemplateOptions="imageTextContentTemplateOptions"
          >
            <template #tooltip>{{ displayText.imageText.tooltip }}</template>
          </ToggleNotificationContentBlock>
        </div>
        <div v-if="!hidePreview">
          <NotifyContentPreviewBlock
            :notifyType="notifyType"
            :contents="syncForm.contents"
            :notifyIndex="notifyIndex"
            :reservationConfig="reservationConfig"
          />
        </div>
      </div>
    </BaseElFormItem>
  </div>
</template>

<script>
import { computed, defineComponent, onMounted, ref, set } from 'vue'
import ToggleNotificationContentBlock from '../components/ToggleNotificationContentBlock.vue'
import SendChannelOptionsBlock from '../components/SendChannelOptionsBlock.vue'
import EnabledNotificationBlock from '../components/EnabledNotificationBlock.vue'
import NotificationAttachConditionBlock from '../components/NotificationAttachConditionBlock.vue'
import SendTimeBlock from '../components/SendTimeBlock.vue'
import ReservationInfoBlock from './components/ReservationInfoBlock.vue'
import UpsellRecommendationBlock from '../components/UpsellRecommendationBlock.vue'
import NotifyContentPreviewBlock from '@/views/ProgressNotificationSettingEdit/components/NotifyContentPreviewBlock.vue'
import { useVModel } from '@vueuse/core'
import { useProgressNotificationEdit } from '@/use/useProgressNotificationEdit'
import { contentBlockTypeConfigs } from '@/config/progressNotification'
import { useRoute } from 'vue-router/composables'
import { map, get, filter, find } from 'lodash'

export default defineComponent({
  name: 'ReservationNotificationSettingEdit',
  components: {
    ToggleNotificationContentBlock,
    SendChannelOptionsBlock,
    EnabledNotificationBlock,
    NotificationAttachConditionBlock,
    ReservationInfoBlock,
    UpsellRecommendationBlock,
    SendTimeBlock,
    NotifyContentPreviewBlock,
  },
  props: {
    isDefault: Boolean,
    notifyIndex: Number,
    form: { type: Object, default: () => ({}) },
    targetTemplateList: { type: Array, default: () => [] },
    contentTemplateList: { type: Array, default: () => [] },
    hideRemove: Boolean,
    reservationConfig: { type: Object, default: () => ({}) },
  },
  setup (props, { emit }) {
    const syncForm = useVModel(props, 'form', emit)
    if (syncForm.value && syncForm.value.contents) {
      syncForm.value.contents = filter(syncForm.value.contents, content => content !== null)
    }
    const appointmentInfoBlock = computed({
      get: () => find(syncForm.value.contents, item => item.type === 'appointmentInfo'),
      set: (newValue) => {
        const index = syncForm.value.contents.findIndex(item => item.type === 'appointmentInfo')
        if (index !== -1) {
          syncForm.value.contents[index] = newValue
        }
      },
    })

    // 表單驗證的 prop 依實際位置綁定：服務開始前多了推薦加購、預約建立多了帳號資訊，圖文資訊的位置不固定
    const contentIndexOf = (type) => syncForm.value.contents.findIndex(item => item.type === type)

    const createComputedForType = (type) => {
      return computed({
        get: () => get(find(syncForm.value.contents, item => item.type === type), 'NotifyContentTemplateId'),
        set: (newValue) => {
          const index = syncForm.value.contents.findIndex(item => item.type === type)
          if (index !== -1) {
            const target = find(syncForm.value.contents, item => item.type === type)
            target.NotifyContentTemplateId = newValue
            set(syncForm.value.contents, index, target)
          }
        },
      })
    }
    const createComputedEnabledForType = (type) => {
      return computed({
        get: () => get(find(syncForm.value.contents, item => item.type === type), 'enabled'),
        set: (newValue) => {
          const index = syncForm.value.contents.findIndex(item => item.type === type)
          if (index !== -1) {
            syncForm.value.contents[index].enabled = newValue
          }
        },
      })
    }
    const shopInfoTemplateId = createComputedForType('shopInfo')
    const shopInfoEnabled = createComputedEnabledForType('shopInfo')
    const memberInfoTemplateId = createComputedForType('memberInfo')
    const memberInfoEnabled = createComputedEnabledForType('memberInfo')
    const upsellRecommendationEnabled = createComputedEnabledForType('upsellRecommendation')
    const imageTextBlockTemplateId = createComputedForType('imageText')
    const imageTextBlockEnabled = createComputedEnabledForType('imageText')

    const contentTypes = computed(() => map(syncForm.value.contents, 'type'))
    const route = useRoute()
    const hidePreview = ref(false)
    const { targetId, newContentData, filterContentTemplateOptions, selectDefaultContentTemplate } = useProgressNotificationEdit()
    const notifyType = computed(() => route.params.type)

    const appointmentInfoContentTemplateOptions = computed(() => filterContentTemplateOptions({
      contentType: contentBlockTypeConfigs.appointmentInfo.value,
      templateDataList: props.contentTemplateList,
    }))

    const shopInfoContentTemplateOptions = computed(() => filterContentTemplateOptions({
      contentType: contentBlockTypeConfigs.shopInfo.value,
      templateDataList: props.contentTemplateList,
    }))

    const imageTextContentTemplateOptions = computed(() => filterContentTemplateOptions({
      contentType: contentBlockTypeConfigs.imageText.value,
      templateDataList: props.contentTemplateList,
    }))

    const displayText = computed(() => {
      const data = {
        delete: '刪除此通知內容',
        notificationContent: '通知內容',
        showPreview: '顯示預覽',
        hidePreview: '隱藏預覽',
        imageText: {
          hint: '「限定對象通知」僅能設置「圖文資訊」，如需變更「預約資訊、店家資訊」的設定，請至可發送給所有通知對象的「預設通知」。',
          tooltip: '如需新增通知內容範本，請至「通知模組 > 通知範本設定 > 通知內容範本」',
        },
        shopInfo: {
          tooltip: '如需新增通知內容範本，請至「通知模組 > 通知範本設定 > 通知內容範本」',
        },
        memberInfo: {
          tooltip: '系統會推播會員姓名與會員手機號碼，使店家可於 LINE 官方帳號管理頁面查詢會員姓名 / 手機號碼找到對應聊天視窗。',
        },
        activeText: '隱藏/顯示',
      }
      return data
    })
    // ?? 參照資料
    const showControl = computed(() => {
      const control = {
        hideRemove: props.hideRemove,
        isDefault: props.isDefault,
        imageText: contentTypes.value.includes('imageText'),
        appointmentInfo: contentTypes.value.includes('appointmentInfo'),
        shopInfo: contentTypes.value.includes('shopInfo'),
        memberInfo: contentTypes.value.includes('memberInfo'),
        upsellRecommendation: contentTypes.value.includes('upsellRecommendation'),
      }
      return control
    })
    onMounted(() => {
      if (!props.isDefault && !targetId.value) {
        // syncForm.value.contents.push(newContentData({ contentType: contentBlockTypeConfigs.appointmentInfo.value, order: 0, typeConfig }))
        // syncForm.value.contents.push(newContentData({ contentType: contentBlockTypeConfigs.shopInfo.value, order: 1 }))
        // syncForm.value.contents.push(newContentData({ contentType: contentBlockTypeConfigs.imageText.value, order: 2 }))
        if (!find(syncForm.value.contents, { type: 'imageText' })) {
          const newContent = newContentData({ contentType: contentBlockTypeConfigs.imageText.value, order: 0 })
          const template = selectDefaultContentTemplate({ templateDataList: props.contentTemplateList, contentType: 'imageText', notifyType: notifyType.value })
          newContent.NotifyContentTemplateId = template
          syncForm.value.contents.push(newContent)
        }
      }

      // for (const idx in syncForm.value.contents) {
      //   if (syncForm.value.contents[idx] && !syncForm.value.contents[idx].NotifyContentTemplateId) {
      //     syncForm.value.contents[idx].NotifyContentTemplateId = selectDefaultContentTemplate({
      //       templateDataList: props.contentTemplateList,
      //       contentType: syncForm.value.contents[idx].type,
      //     })
      //   }
      // }
    })

    return {
      targetId,
      syncForm,
      contentIndexOf,
      contentBlockTypeConfigs,
      appointmentInfoContentTemplateOptions,
      shopInfoContentTemplateOptions,
      imageTextContentTemplateOptions,
      appointmentInfoBlock,
      shopInfoTemplateId,
      shopInfoEnabled,
      memberInfoTemplateId,
      memberInfoEnabled,
      upsellRecommendationEnabled,
      imageTextBlockTemplateId,
      imageTextBlockEnabled,
      hidePreview,
      contentTypes,
      notifyType,
      displayText,
      showControl,
    }
  },
})
</script>

<style lang="postcss" scoped>
.remove-setting-block-btn {
  @apply absolute right-0 top-0;
  @apply flex items-center gap-[4px] w-[fit-content];
  @apply bg-gray-20 text-gray-80;
  @apply py-[3.5px] px-[8px] cursor-pointer rounded-[4px];
}
.remove-setting-block-btn:hover {
  @apply opacity-80;
}

.hint-text {
  @apply text-gray-60 leading-[17.38px] py-[20px] font-medium;
}

::v-deep .block-form-item .el-form-item__label {
  @apply block;
}
</style>
